<script lang="ts">
/**
 * How the outcome reads. `neutral` is "nothing went wrong, you just have
 * something to do" — a sent link, a pending verification. Views pick a member;
 * this component never reads a backend value.
 */
export type GaiaAuthStatusTone = 'success' | 'warning' | 'alert' | 'neutral';
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaHeader from '../../base/GaiaHeader.vue';
import GaiaText from '../../base/GaiaText.vue';
import { gaiaAmber, gaiaNeutral, gaiaStatus } from '../../themes/tokens';

const props = defineProps({
  /** Serif headline, e.g. "Email verified". Omitted while loading. */
  title: {
    type: String,
    default: undefined,
  },
  tone: {
    type: String as PropType<GaiaAuthStatusTone>,
    default: 'neutral',
  },
  /**
   * The link is still being checked. Swaps the disc for a spinner and shows
   * only the caption — a title would claim an outcome that hasn't happened.
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * The form this block takes inside a dialog: a smaller disc and title, since
   * a modal is already a smaller frame than the card it opens over.
   */
  compact: {
    type: Boolean,
    default: false,
  },
});

// Disc tint + glyph per tone. The auth screens are the only place a status
// reads as a full-card outcome rather than a row-level dot, which is why the
// disc lives here and not in GaiaStatusIndicator.
const palette = {
  success: { tint: gaiaStatus.successTint, ink: gaiaStatus.success },
  warning: { tint: gaiaStatus.warningTint, ink: gaiaAmber.strong },
  alert: { tint: gaiaStatus.alertTint, ink: gaiaStatus.alert },
  neutral: { tint: gaiaNeutral.surfaceHoverStrong, ink: gaiaNeutral.text },
} as const;

const colors = computed(() => palette[props.tone]);

const track = gaiaNeutral.surfaceTrack;
const ink = gaiaNeutral.ink;
</script>

<template>
  <!-- The outcome block every auth screen ends on: a tinted disc, the serif
       line that names what happened, the explanation, then the actions. Used
       by verify / invite / reset / OAuth-callback, which between them are the
       same block a dozen times over with a different glyph and copy. -->
  <n-flex vertical align="center" :size="0" :style="{ textAlign: 'center' }">
    <span v-if="props.loading" class="spinner" />
    <n-flex
      v-else
      align="center"
      justify="center"
      :size="0"
      :wrap="false"
      class="disc"
      :class="{ compact: props.compact }"
      :style="{ background: colors.tint, color: colors.ink }"
    >
      <slot name="icon" />
    </n-flex>

    <!-- Explicit margin: n-h* carries naive's Typography headerMargin. -->
    <gaia-header
      v-if="props.title && !props.loading"
      :level="1"
      variant="display"
      :size="props.compact ? 'dialog' : 'page'"
      :style="{ margin: '0 0 6px' }"
    >
      {{ props.title }}
    </gaia-header>

    <gaia-text
      v-if="$slots.default"
      size="body"
      tone="tertiary"
      :style="{ display: 'block', lineHeight: 1.6 }"
    >
      <slot />
    </gaia-text>

    <n-flex
      v-if="$slots.actions"
      vertical
      :size="14"
      :style="{ width: '100%', marginTop: props.compact ? '18px' : '22px' }"
    >
      <slot name="actions" />
    </n-flex>
  </n-flex>
</template>

<style scoped>
/* A fixed 44px disc centring whatever glyph is slotted — the tint and ink come
   from the tone, so only the box is here. */
.disc {
  width: 44px;
  height: 44px;
  flex: none;
  border-radius: 50%;
  margin: 4px 0 16px;
}

.disc.compact {
  width: 40px;
  height: 40px;
  margin: 4px 0 14px;
}

/* The pre-outcome state: a ring rather than a disc, so a link still being
   checked never looks like a verdict. naive's n-spin draws its own icon and
   can't be retuned to this weight. */
.spinner {
  width: 34px;
  height: 34px;
  margin: 14px 0 18px;
  border: 3px solid v-bind(track);
  border-top-color: v-bind(ink);
  border-radius: 50%;
  animation: gaia-auth-spin 0.8s linear infinite;
}

@keyframes gaia-auth-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
